/**
 * CombinedPasteStep - UNIFIED page for ALL credit data entry
 * Contains: Paste Client Info + Quick Paste Parser + Manual Account Entry + Credit Score Input
 * This is the single point of entry for ALL credit report data
 * Shows AFTER Handwritten Declaration and Signature Selection in Dispute Wizard
 */

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  User, CreditCard, Sparkles, Loader2, ClipboardPaste, 
  Check, AlertTriangle, ChevronDown, ChevronUp, Copy, 
  Car, Phone, Home, Briefcase, GraduationCap, Heart, 
  Building, HelpCircle, Zap, DollarSign, Shield, Pencil,
  FileText, TrendingUp, Save, Plus, Trash2, Upload
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Account type detection keywords
const ACCOUNT_TYPE_KEYWORDS = {
  CREDIT_CARD: ['card', 'credit', 'visa', 'mastercard', 'amex', 'discover', 'capital one', 'chase', 'citi', 'synchrony', 'syncb', 'barclays', 'wells fargo'],
  AUTO: ['auto', 'car', 'vehicle', 'toyota', 'honda', 'ford', 'gm', 'ally', 'santander', 'carmax', 'carvana'],
  STUDENT_LOAN: ['student', 'loan', 'navient', 'nelnet', 'sallie mae', 'great lakes', 'fedloan', 'mohela', 'education', 'aidvantage', 'dept of ed'],
  MOBILE: ['mobile', 'phone', 'wireless', 'verizon', 't-mobile', 'at&t', 'sprint', 'cellular'],
  UTILITY: ['utility', 'electric', 'gas', 'water', 'power', 'energy', 'pge', 'con ed', 'duke energy'],
  MEDICAL: ['medical', 'hospital', 'health', 'doctor', 'clinic', 'dental', 'healthcare'],
  RENTAL: ['rental', 'rent', 'apartment', 'lease', 'property'],
  REAL_ESTATE: ['mortgage', 'home loan', 'real estate', 'quicken', 'rocket', 'countrywide'],
  LOAN_LEASE: ['loan', 'lease', 'personal loan', 'installment', 'finance', 'avant', 'lending'],
  INSURANCE: ['insurance', 'geico', 'state farm', 'progressive', 'allstate'],
  OTHER: []
};

// Fraud category options
const FRAUD_CATEGORIES = [
  { value: "CREDIT_CARD", label: "Credit Card", icon: CreditCard },
  { value: "AUTO", label: "Auto", icon: Car },
  { value: "LOAN_LEASE", label: "Loan/Lease", icon: DollarSign },
  { value: "MOBILE", label: "Mobile/Phone", icon: Phone },
  { value: "UTILITY", label: "Utility", icon: Zap },
  { value: "RENTAL", label: "Rental", icon: Home },
  { value: "MEDICAL", label: "Medical", icon: Heart },
  { value: "STUDENT_LOAN", label: "Student Loan", icon: GraduationCap },
  { value: "REAL_ESTATE", label: "Real Estate", icon: Building },
  { value: "INSURANCE", label: "Insurance", icon: Shield },
  { value: "OTHER", label: "Other", icon: HelpCircle }
];

// Detect account type from account name
const detectAccountType = (accountName) => {
  const nameLower = accountName.toLowerCase();
  
  for (const [type, keywords] of Object.entries(ACCOUNT_TYPE_KEYWORDS)) {
    for (const keyword of keywords) {
      if (nameLower.includes(keyword)) {
        return type;
      }
    }
  }
  
  return 'OTHER';
};

export default function CombinedPasteStep({ 
  clientId,
  client,
  setClient,
  onAccountsParsed,
  existingAccounts = [],
  showClientInfo = true,
  showAccountParser = true,
  // NEW: Credit score props
  manualScores = { equifax: '', experian: '', transunion: '' },
  setManualScores,
  onSaveScores,
  bureauUploadStatus = {}
}) {
  // Client Info Paste state
  const [showClientPaste, setShowClientPaste] = useState(false);
  const [clientPasteText, setClientPasteText] = useState('');
  const [parsingClient, setParsingClient] = useState(false);
  
  // Account Parser state
  const [showAccountPaste, setShowAccountPaste] = useState(true);
  const [accountPasteText, setAccountPasteText] = useState('');
  const [parsingAccounts, setParsingAccounts] = useState(false);
  const [parsedAccounts, setParsedAccounts] = useState([]);
  const [showParsedResults, setShowParsedResults] = useState(false);
  
  // Manual Account Entry state
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualAccount, setManualAccount] = useState({
    account_name: '',
    open_date: '',
    balance: '',
    account_type: 'OTHER'
  });
  const [addingManual, setAddingManual] = useState(false);
  
  // Credit Score state
  const [showScoreSection, setShowScoreSection] = useState(true);
  const [localScores, setLocalScores] = useState({
    equifax: manualScores?.equifax || '',
    experian: manualScores?.experian || '',
    transunion: manualScores?.transunion || ''
  });
  const [savingScores, setSavingScores] = useState(false);

  // Sync local scores with parent
  useEffect(() => {
    if (manualScores) {
      setLocalScores({
        equifax: manualScores.equifax || '',
        experian: manualScores.experian || '',
        transunion: manualScores.transunion || ''
      });
    }
  }, [manualScores]);

  // Parse client info
  const handleParseClientInfo = async () => {
    if (!clientPasteText.trim()) {
      toast.error('Please paste client information');
      return;
    }
    
    setParsingClient(true);
    try {
      const response = await axios.post(`${API}/api/smart-parse`, {
        text: clientPasteText
      });
      
      if (response.data.success && response.data.data) {
        const p = response.data.data;
        
        // Convert date from MM/DD/YYYY to YYYY-MM-DD
        let formattedDob = p.date_of_birth || '';
        if (formattedDob && formattedDob.includes('/')) {
          const parts = formattedDob.split('/');
          if (parts.length === 3) {
            formattedDob = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
          }
        }
        
        setClient(prev => ({
          ...prev,
          name: p.name || prev.name || '',
          email: p.email || prev.email || '',
          phone: p.phone || prev.phone || '',
          address: p.address || prev.address || '',
          city: p.city || prev.city || '',
          state: p.state || prev.state || '',
          zip_code: p.zip_code || prev.zip_code || '',
          date_of_birth: formattedDob || prev.date_of_birth || '',
          ssn_last4: p.ssn_last4 || prev.ssn_last4 || '',
          ssn_full: p.ssn_full || prev.ssn_full || '',
        }));
        
        setShowClientPaste(false);
        setClientPasteText('');
        toast.success(`Client info parsed: ${p.name || 'Success'}`);
      } else {
        toast.error('Could not parse client info. Check the format.');
      }
    } catch (error) {
      console.error('Parse error:', error);
      toast.error('Failed to parse client information');
    } finally {
      setParsingClient(false);
    }
  };

  // Parse credit report accounts
  const handleParseAccounts = async () => {
    if (!accountPasteText.trim() || accountPasteText.trim().length < 20) {
      toast.error('Please paste credit report text (at least 20 characters)');
      return;
    }
    
    setParsingAccounts(true);
    try {
      const response = await axios.post(`${API}/api/credit-report/parse-accounts`, {
        text: accountPasteText,
        client_id: clientId
      });
      
      if (response.data.success) {
        // Add detected account types to each account
        const accountsWithTypes = (response.data.accounts || []).map(acc => ({
          ...acc,
          detected_type: detectAccountType(acc.account_name || ''),
          selected_type: detectAccountType(acc.account_name || '')
        }));
        
        setParsedAccounts(accountsWithTypes);
        setShowParsedResults(true);
        
        const complete = response.data.complete_accounts || 0;
        const incomplete = response.data.incomplete_accounts || 0;
        
        toast.success(`Found ${complete} accounts${incomplete > 0 ? ` (${incomplete} incomplete)` : ''}`);
        
        // Notify parent of parsed accounts
        if (onAccountsParsed && complete > 0) {
          onAccountsParsed(accountsWithTypes.filter(a => !a.incomplete));
        }
      } else {
        toast.error(response.data.error || 'Failed to parse accounts');
      }
    } catch (error) {
      console.error('Parse error:', error);
      toast.error('Failed to parse credit report');
    } finally {
      setParsingAccounts(false);
    }
  };

  // Update account type
  const handleUpdateAccountType = (index, newType) => {
    setParsedAccounts(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], selected_type: newType };
      return updated;
    });
  };

  // Copy parsed results
  const copyParsedResults = () => {
    const lines = parsedAccounts
      .filter(a => !a.incomplete)
      .map(a => a.formatted)
      .join('\n');
    navigator.clipboard.writeText(lines);
    toast.success('Copied to clipboard!');
  };

  // Add manual account
  const handleAddManualAccount = async () => {
    if (!manualAccount.account_name.trim()) {
      toast.error('Please enter an account name');
      return;
    }
    
    setAddingManual(true);
    try {
      // Save to backend
      const response = await axios.post(`${API}/api/accounts`, {
        client_id: clientId,
        creditor_name: manualAccount.account_name,
        date_opened: manualAccount.open_date || null,
        balance: parseFloat(manualAccount.balance.replace(/[^0-9.-]/g, '')) || 0,
        account_type: manualAccount.account_type,
        bureaus_present: ['equifax', 'experian', 'transunion'],
        is_manual: true
      });
      
      toast.success(`Added: ${manualAccount.account_name}`);
      
      // Reset form
      setManualAccount({
        account_name: '',
        open_date: '',
        balance: '',
        account_type: 'OTHER'
      });
      
      // Refresh accounts
      if (onAccountsParsed) {
        onAccountsParsed([]);
      }
    } catch (error) {
      console.error('Error adding manual account:', error);
      toast.error('Failed to add account');
    } finally {
      setAddingManual(false);
    }
  };

  // Save credit scores
  const handleSaveScores = async () => {
    setSavingScores(true);
    try {
      if (setManualScores) {
        setManualScores(localScores);
      }
      
      if (onSaveScores) {
        await onSaveScores(localScores);
      } else {
        // Default: save to client record
        await axios.patch(`${API}/api/clients/${clientId}`, {
          starting_score_equifax: localScores.equifax ? parseInt(localScores.equifax) : null,
          starting_score_experian: localScores.experian ? parseInt(localScores.experian) : null,
          starting_score_transunion: localScores.transunion ? parseInt(localScores.transunion) : null
        });
        toast.success('Credit scores saved');
      }
    } catch (error) {
      console.error('Error saving scores:', error);
      toast.error('Failed to save scores');
    } finally {
      setSavingScores(false);
    }
  };

  // Get icon for category
  const getCategoryIcon = (categoryValue) => {
    const category = FRAUD_CATEGORIES.find(c => c.value === categoryValue);
    return category ? category.icon : HelpCircle;
  };

  return (
    <div className="space-y-6">
      {/* Section Title */}
      <Card className="bg-gradient-to-r from-zinc-900 to-zinc-800 border-zinc-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl text-white flex items-center gap-2">
            <ClipboardPaste className="w-6 h-6 text-cyan-400" />
            Import Credit Report Data
          </CardTitle>
          <p className="text-sm text-zinc-400">
            All your credit data entry in one place - paste client info, accounts, or enter manually
          </p>
        </CardHeader>
      </Card>

      {/* SECTION 1: Credit Scores */}
      <Card className="bg-zinc-900 border-purple-500/30">
        <CardHeader 
          className="pb-2 cursor-pointer hover:bg-zinc-800/50 transition-colors rounded-t-lg"
          onClick={() => setShowScoreSection(!showScoreSection)}
        >
          <CardTitle className="text-lg text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-400" />
              Credit Scores
              {(localScores.equifax || localScores.experian || localScores.transunion) && (
                <span className="text-xs bg-green-600/20 text-green-400 px-2 py-0.5 rounded ml-2">
                  {[localScores.equifax, localScores.experian, localScores.transunion].filter(Boolean).length} saved
                </span>
              )}
            </div>
            {showScoreSection ? (
              <ChevronUp className="w-5 h-5 text-zinc-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-zinc-400" />
            )}
          </CardTitle>
        </CardHeader>
        
        {showScoreSection && (
          <CardContent className="space-y-4">
            <p className="text-sm text-zinc-400">
              Enter your current credit scores from each bureau:
            </p>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-sm text-red-400 font-medium mb-1 block">Equifax Score</Label>
                <Input
                  type="number"
                  min="300"
                  max="850"
                  placeholder="e.g. 650"
                  value={localScores.equifax}
                  onChange={(e) => setLocalScores(prev => ({ ...prev, equifax: e.target.value }))}
                  className="border-red-500/30 bg-zinc-800"
                  data-testid="equifax-score-input"
                />
              </div>
              <div>
                <Label className="text-sm text-blue-400 font-medium mb-1 block">Experian Score</Label>
                <Input
                  type="number"
                  min="300"
                  max="850"
                  placeholder="e.g. 650"
                  value={localScores.experian}
                  onChange={(e) => setLocalScores(prev => ({ ...prev, experian: e.target.value }))}
                  className="border-blue-500/30 bg-zinc-800"
                  data-testid="experian-score-input"
                />
              </div>
              <div>
                <Label className="text-sm text-yellow-400 font-medium mb-1 block">TransUnion Score</Label>
                <Input
                  type="number"
                  min="300"
                  max="850"
                  placeholder="e.g. 650"
                  value={localScores.transunion}
                  onChange={(e) => setLocalScores(prev => ({ ...prev, transunion: e.target.value }))}
                  className="border-yellow-500/30 bg-zinc-800"
                  data-testid="transunion-score-input"
                />
              </div>
            </div>
            
            {/* Show current scores if available from bureau upload */}
            {(bureauUploadStatus?.equifax?.score || bureauUploadStatus?.experian?.score || bureauUploadStatus?.transunion?.score) && (
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-sm text-green-400 font-medium">Detected from Report:</p>
                <div className="flex gap-4 mt-1">
                  {bureauUploadStatus?.equifax?.score && <span className="text-sm">Equifax: {bureauUploadStatus.equifax.score}</span>}
                  {bureauUploadStatus?.experian?.score && <span className="text-sm">Experian: {bureauUploadStatus.experian.score}</span>}
                  {bureauUploadStatus?.transunion?.score && <span className="text-sm">TransUnion: {bureauUploadStatus.transunion.score}</span>}
                </div>
              </div>
            )}
            
            <Button 
              onClick={handleSaveScores}
              className="w-full bg-purple-600 hover:bg-purple-700"
              disabled={savingScores || (!localScores.equifax && !localScores.experian && !localScores.transunion)}
            >
              {savingScores ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Credit Scores
                </>
              )}
            </Button>
          </CardContent>
        )}
      </Card>

      {/* SECTION 2: Paste Client Info */}
      {showClientInfo && (
        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader 
            className="pb-2 cursor-pointer hover:bg-zinc-800/50 transition-colors rounded-t-lg"
            onClick={() => setShowClientPaste(!showClientPaste)}
          >
            <CardTitle className="text-lg text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-green-400" />
                Paste Client Information
                {client?.name && (
                  <span className="text-xs bg-green-600/20 text-green-400 px-2 py-0.5 rounded ml-2">
                    {client.name}
                  </span>
                )}
              </div>
              {showClientPaste ? (
                <ChevronUp className="w-5 h-5 text-zinc-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-zinc-400" />
              )}
            </CardTitle>
          </CardHeader>
          
          {showClientPaste && (
            <CardContent className="space-y-4">
              <div className="text-xs text-zinc-400 bg-zinc-800 p-3 rounded">
                <strong className="text-blue-400">Paste in this format:</strong><br/>
                NAME - John Doe<br/>
                DATE OF BIRTH - 01/15/1985<br/>
                SOCIAL SECURITY NUMBER - 123-45-6789<br/>
                ADDRESS - 123 Main Street<br/>
                City, ST 12345<br/>
                PHONE NUMBER - 555-123-4567<br/>
                EMAIL - john@example.com
              </div>
              
              <Textarea
                value={clientPasteText}
                onChange={(e) => setClientPasteText(e.target.value)}
                placeholder={`NAME - Damien Salter
DATE OF BIRTH - 06/29/1978
SOCIAL SECURITY NUMBER - 561-53-6166
ADDRESS - 4202 S Victoria Ave
View Park, CA 90008
PHONE NUMBER - 323-209-8462
EMAIL - Dsalterbeauty@aol.com`}
                className="min-h-[180px] bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
                data-testid="client-paste-textarea"
              />
              
              <div className="flex gap-3">
                <Button
                  onClick={handleParseClientInfo}
                  disabled={parsingClient || !clientPasteText.trim()}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {parsingClient ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Parsing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Parse & Fill Client Info
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => setClientPasteText('')}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300"
                >
                  Clear
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* SECTION 3: Quick Paste Parser for Accounts */}
      {showAccountParser && (
        <Card className="bg-zinc-900 border-cyan-500/30">
          <CardHeader 
            className="pb-2 cursor-pointer hover:bg-zinc-800/50 transition-colors rounded-t-lg"
            onClick={() => setShowAccountPaste(!showAccountPaste)}
          >
            <CardTitle className="text-lg text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-cyan-400" />
                Quick Paste Parser
                <span className="text-xs bg-cyan-600/20 text-cyan-400 px-2 py-0.5 rounded ml-2">
                  Credit Karma / Experian
                </span>
                {parsedAccounts.length > 0 && (
                  <span className="text-xs bg-green-600/20 text-green-400 px-2 py-0.5 rounded">
                    {parsedAccounts.filter(a => !a.incomplete).length} accounts
                  </span>
                )}
              </div>
              {showAccountPaste ? (
                <ChevronUp className="w-5 h-5 text-zinc-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-zinc-400" />
              )}
            </CardTitle>
            <p className="text-xs text-zinc-400">
              Paste raw credit report text to extract accounts as: ACCOUNT NAME-OPEN DATE-$BALANCE
            </p>
          </CardHeader>
          
          {showAccountPaste && (
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">
                  Paste your credit report text below (Credit Karma, Experian, or any format):
                </label>
                <Textarea
                  value={accountPasteText}
                  onChange={(e) => setAccountPasteText(e.target.value)}
                  placeholder={`Paste credit report text here...

Example format:
CAPITAL ONE
Reported: Mar. 17, 2026
$1,712.00
Closed

Opened
May 24, 2018

Balance
$1712

NCB MANAGEMENT SERVICES
Collection
Opened: Jun 24, 2025
Balance: $1,875`}
                  className="min-h-[200px] bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
                  data-testid="account-paste-textarea"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={handleParseAccounts}
                  disabled={parsingAccounts || !accountPasteText.trim()}
                  className="bg-cyan-600 hover:bg-cyan-700"
                  data-testid="parse-accounts-btn"
                >
                  {parsingAccounts ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Parsing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Parse Accounts
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={() => {
                    setAccountPasteText('');
                    setParsedAccounts([]);
                    setShowParsedResults(false);
                  }}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300"
                >
                  Clear
                </Button>
              </div>
              
              {/* Parsed Results with Account Type Selection */}
              {showParsedResults && parsedAccounts.length > 0 && (
                <div className="mt-4 p-4 bg-zinc-800 rounded-lg border border-zinc-700">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-white flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Extracted Accounts ({parsedAccounts.filter(a => !a.incomplete).length})
                    </h4>
                    <Button
                      onClick={copyParsedResults}
                      size="sm"
                      variant="outline"
                      className="border-zinc-600 text-zinc-300 text-xs"
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copy All
                    </Button>
                  </div>
                  
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {parsedAccounts.map((acc, idx) => {
                      const CategoryIcon = getCategoryIcon(acc.selected_type);
                      return (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg border ${
                            acc.incomplete 
                              ? 'bg-yellow-900/20 border-yellow-700/50' 
                              : 'bg-zinc-900/50 border-zinc-600'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <CategoryIcon className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                                <span className="font-mono text-sm text-white truncate">
                                  {acc.account_name}
                                </span>
                                {acc.confidence && (
                                  <span className="text-xs text-zinc-500">
                                    ({Math.round(acc.confidence * 100)}%)
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-zinc-400 font-mono">
                                Opened: {acc.open_date || 'Unknown'} | Balance: {acc.balance || '$0'}
                              </div>
                            </div>
                            
                            {/* Account Type Selector */}
                            {!acc.incomplete && (
                              <div className="flex-shrink-0 w-40">
                                <Select
                                  value={acc.selected_type}
                                  onValueChange={(value) => handleUpdateAccountType(idx, value)}
                                >
                                  <SelectTrigger className="h-8 bg-zinc-800 border-zinc-600 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-zinc-800 border-zinc-600">
                                    {FRAUD_CATEGORIES.map(cat => {
                                      const Icon = cat.icon;
                                      return (
                                        <SelectItem 
                                          key={cat.value} 
                                          value={cat.value}
                                          className="text-xs"
                                        >
                                          <div className="flex items-center gap-2">
                                            <Icon className="w-3 h-3" />
                                            {cat.label}
                                          </div>
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                          
                          {acc.incomplete && (
                            <div className="mt-2 text-xs text-yellow-400 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Missing open date - not added to dispute list
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  <p className="text-xs text-zinc-500 mt-3">
                    Complete accounts have been added to your dispute list. You can change account types using the dropdown.
                  </p>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* SECTION 4: Manual Account Entry */}
      <Card className="bg-zinc-900 border-amber-500/30">
        <CardHeader 
          className="pb-2 cursor-pointer hover:bg-zinc-800/50 transition-colors rounded-t-lg"
          onClick={() => setShowManualEntry(!showManualEntry)}
        >
          <CardTitle className="text-lg text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-amber-400" />
              Add Account Manually
            </div>
            {showManualEntry ? (
              <ChevronUp className="w-5 h-5 text-zinc-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-zinc-400" />
            )}
          </CardTitle>
        </CardHeader>
        
        {showManualEntry && (
          <CardContent className="space-y-4">
            <p className="text-sm text-zinc-400">
              Add individual accounts that weren't captured by the parser:
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-sm text-zinc-300">Account/Creditor Name</Label>
                <Input
                  value={manualAccount.account_name}
                  onChange={(e) => setManualAccount(prev => ({ ...prev, account_name: e.target.value }))}
                  placeholder="e.g. CAPITAL ONE"
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="manual-account-name"
                />
              </div>
              <div>
                <Label className="text-sm text-zinc-300">Open Date</Label>
                <Input
                  value={manualAccount.open_date}
                  onChange={(e) => setManualAccount(prev => ({ ...prev, open_date: e.target.value }))}
                  placeholder="MM/DD/YYYY or YYYY-MM-DD"
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="manual-account-date"
                />
              </div>
              <div>
                <Label className="text-sm text-zinc-300">Balance</Label>
                <Input
                  value={manualAccount.balance}
                  onChange={(e) => setManualAccount(prev => ({ ...prev, balance: e.target.value }))}
                  placeholder="e.g. $1,500"
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="manual-account-balance"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-sm text-zinc-300">Account Type</Label>
                <Select
                  value={manualAccount.account_type}
                  onValueChange={(value) => setManualAccount(prev => ({ ...prev, account_type: value }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-600">
                    {FRAUD_CATEGORIES.map(cat => {
                      const Icon = cat.icon;
                      return (
                        <SelectItem key={cat.value} value={cat.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            {cat.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Button
              onClick={handleAddManualAccount}
              disabled={addingManual || !manualAccount.account_name.trim()}
              className="w-full bg-amber-600 hover:bg-amber-700"
            >
              {addingManual ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Account
                </>
              )}
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Existing Accounts Summary */}
      {existingAccounts.length > 0 && (
        <Card className="bg-zinc-900 border-green-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-400" />
              Current Accounts ({existingAccounts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-zinc-400">
              You have {existingAccounts.length} accounts ready for dispute. Continue to the next step to select which accounts to include in your dispute letters.
            </div>
            
            {/* Quick preview of accounts */}
            <div className="mt-3 max-h-[200px] overflow-y-auto space-y-2">
              {existingAccounts.slice(0, 10).map((acc, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-zinc-800 rounded text-sm">
                  <span className="text-white truncate max-w-[60%]">{acc.creditor_name || acc.canonical_name || 'Unknown'}</span>
                  <span className="text-zinc-400">${acc.balance || 0}</span>
                </div>
              ))}
              {existingAccounts.length > 10 && (
                <p className="text-xs text-zinc-500 text-center">
                  + {existingAccounts.length - 10} more accounts
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
